import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  Building2,
  Users,
  FileText,
  Menu,
  X,
  ShoppingBag,
  LogOut,
  Edit,
  Calculator,
  Wallet
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Custom Receipt Icon with Rupee symbol
const ReceiptIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z" />
    <path d="M14 8H8" />
    <path d="M16 12H8" />
    <path d="M13 16H8" />
    <text x="15.5" y="9.5" fontSize="7" fontWeight="bold" fill="currentColor" textAnchor="middle">₹</text>
  </svg>
);

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { logout, company } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Company Profile', href: '/profile', icon: Building2 },
    { name: 'Clients', href: '/clients', icon: Users },
    { name: 'Goods/Services', href: '/services', icon: ShoppingBag },
    { name: 'All Invoices', href: '/invoices', icon: FileText },
    { name: 'Create Invoice', href: '/invoice/create', icon: FileText },
    { name: 'All Receipts', href: '/receipts', icon: ReceiptIcon },
    { name: 'Expenses', href: '/expenses', icon: Wallet },
    { name: 'Accounts', href: '/accounts', icon: Calculator },
    { name: 'Revised Invoice & Receipt', href: '/revised', icon: Edit },
  ];

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Mobile sidebar */}
      <motion.div
        initial={false}
        animate={{ x: sidebarOpen ? 0 : -300 }}
        className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
      >
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        )}
        <div className="relative flex w-72 flex-col bg-white shadow-2xl border-r border-slate-200 h-full">
          <div className="flex h-20 items-center justify-between px-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Invoice Generator
            </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-slate-500 hover:text-slate-700 transition-colors p-2 hover:bg-slate-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl mb-1.5 transition-all duration-200 ${
                    active
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-indigo-600'
                  }`}
                >
                  <Icon className={`mr-3 h-5 w-5 ${active ? 'text-white' : 'text-slate-500 group-hover:text-indigo-600'}`} />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 text-sm font-medium text-slate-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-80 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col flex-grow bg-white border-r border-slate-200 shadow-sm">
          <div className="flex items-center h-24 px-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Invoice Generator
              </h1>
            </div>
          </div>
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            {navigation.map((item, index) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={item.href}
                    className={`group flex items-center px-4 py-3.5 text-sm font-medium rounded-xl mb-2 transition-all duration-200 ${
                      active
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-indigo-600'
                    }`}
                  >
                    <Icon className={`mr-3 h-5 w-5 ${active ? 'text-white' : 'text-slate-500 group-hover:text-indigo-600'}`} />
                    {item.name}
                  </Link>
                </motion.div>
              );
            })}
          </nav>
          <div className="p-4 border-t border-slate-200 bg-gradient-to-br from-slate-50 to-white">
            {company && (
              <div className="px-4 py-3 mb-3 rounded-xl bg-white border border-slate-200 shadow-sm">
                <div className="text-sm font-semibold text-slate-900 mb-1">
                  {company.companyName}
                </div>
                <div className="text-xs text-slate-500">
                  {new Date().toLocaleDateString('en-IN', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 text-sm font-medium text-slate-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-80 flex flex-col flex-1 min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-slate-200 px-4 py-4 lg:px-8 shadow-sm">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-slate-600 hover:text-slate-900 lg:hidden transition-colors p-2 hover:bg-slate-100 rounded-lg"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-3 ml-auto">
              {company && (
                <div className="hidden sm:flex items-center text-sm font-semibold text-slate-900 px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100">
                  <Building2 className="h-4 w-4 mr-2 text-indigo-600" />
                  {company.companyName}
                </div>
              )}
              <div className="text-sm text-slate-600 px-4 py-2 rounded-lg bg-slate-50 border border-slate-200">
                {new Date().toLocaleDateString('en-IN', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gradient-to-br from-slate-50 via-white to-slate-50">
          <div className="px-4 py-8 lg:px-10">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
